/**
 * Camera Overlay SVG Symbol Library
 *
 * This file contains SVG components for each camera movement type.
 * Each symbol is designed to be clear, recognizable, and visible on most images.
 *
 * Design principles:
 * - 80x80px default size (scales with the `scale` prop)
 * - Stroke-based design for clarity at all sizes
 * - Default white color (#FFFFFF) for visibility
 * - Semi-transparent (0.8 opacity) to not obscure image details
 * - Simple, professional symbols based on film industry standards
 */

import type { CameraMovementType } from '@/types';

export interface OverlaySymbolProps {
  /** Hex color for the symbol, default '#FFFFFF' (white) */
  color?: string;
  /** Opacity 0-1, default 0.8 */
  opacity?: number;
  /** Size in pixels, default 80 */
  size?: number;
}

/**
 * Pan Left - Simple horizontal arrow
 */
export const PanLeftSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="65" y1="40" x2="15" y2="40" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="25,30 15,40 25,50" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Pan Right - Simple horizontal arrow
 */
export const PanRightSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="15" y1="40" x2="65" y2="40" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="55,30 65,40 55,50" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Tilt Up - Simple vertical arrow
 */
export const TiltUpSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="40" y1="65" x2="40" y2="15" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="30,25 40,15 50,25" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Tilt Down - Simple vertical arrow
 */
export const TiltDownSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="40" y1="15" x2="40" y2="65" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="30,55 40,65 50,55" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Zoom In - Arrows pointing inward to smaller frame (industry standard)
 */
export const ZoomInSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    {/* Outer frame (starting frame) */}
    <rect x="10" y="10" width="60" height="60" stroke={color} strokeWidth="2" fill="none" strokeDasharray="4,4" opacity="0.5" />

    {/* Inner frame (end frame - smaller) */}
    <rect x="25" y="25" width="30" height="30" stroke={color} strokeWidth="3" fill="none" />

    {/* Arrows pointing inward to corners of inner frame */}
    {/* Top-left arrow */}
    <line x1="12" y1="12" x2="22" y2="22" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="18,22 22,22 22,18" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Top-right arrow */}
    <line x1="68" y1="12" x2="58" y2="22" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="62,22 58,22 58,18" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Bottom-right arrow */}
    <line x1="68" y1="68" x2="58" y2="58" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="58,62 58,58 62,58" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Bottom-left arrow */}
    <line x1="12" y1="68" x2="22" y2="58" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="22,62 22,58 18,58" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Zoom Out - Arrows pointing outward from smaller to larger frame (industry standard)
 */
export const ZoomOutSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    {/* Inner frame (starting frame - smaller) */}
    <rect x="25" y="25" width="30" height="30" stroke={color} strokeWidth="3" fill="none" />

    {/* Outer frame (end frame - larger) */}
    <rect x="10" y="10" width="60" height="60" stroke={color} strokeWidth="2" fill="none" strokeDasharray="4,4" opacity="0.5" />

    {/* Arrows pointing outward from corners of inner frame */}
    {/* Top-left arrow */}
    <line x1="25" y1="25" x2="15" y2="15" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="15,19 15,15 19,15" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Top-right arrow */}
    <line x1="55" y1="25" x2="65" y2="15" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="61,15 65,15 65,19" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Bottom-right arrow */}
    <line x1="55" y1="55" x2="65" y2="65" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="65,61 65,65 61,65" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />

    {/* Bottom-left arrow */}
    <line x1="25" y1="55" x2="15" y2="65" stroke={color} strokeWidth="3" strokeLinecap="round" />
    <polyline points="19,65 15,65 15,61" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Push In - Four arrows pointing inward from corners (camera movement toward subject)
 */
export const PushInSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    {/* Top-left arrow */}
    <path
      d="M 10 10 L 30 10 L 30 15 L 17 28 L 15 30 L 10 30 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />

    {/* Top-right arrow */}
    <path
      d="M 70 10 L 50 10 L 50 15 L 63 28 L 65 30 L 70 30 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />

    {/* Bottom-right arrow */}
    <path
      d="M 70 70 L 70 50 L 65 50 L 52 63 L 50 65 L 50 70 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />

    {/* Bottom-left arrow */}
    <path
      d="M 10 70 L 10 50 L 15 50 L 28 63 L 30 65 L 30 70 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Pull Back - Four arrows pointing outward to corners (camera movement away from subject)
 */
export const PullBackSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    {/* Top-left arrow */}
    <path
      d="M 30 30 L 10 30 L 10 10 L 30 10 L 30 15 L 17 15 L 15 15 L 15 17 L 15 30 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />

    {/* Top-right arrow */}
    <path
      d="M 50 30 L 70 30 L 70 10 L 50 10 L 50 15 L 63 15 L 65 15 L 65 17 L 65 30 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />

    {/* Bottom-right arrow */}
    <path
      d="M 50 50 L 70 50 L 70 70 L 50 70 L 50 65 L 63 65 L 65 65 L 65 63 L 65 50 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />

    {/* Bottom-left arrow */}
    <path
      d="M 30 50 L 10 50 L 10 70 L 30 70 L 30 65 L 17 65 L 15 65 L 15 63 L 15 50 Z"
      fill={color}
      stroke="#000000"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Truck Left - Simple horizontal arrow pointing left
 */
export const TruckLeftSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="65" y1="40" x2="15" y2="40" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="25,30 15,40 25,50" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Truck Right - Simple horizontal arrow pointing right
 */
export const TruckRightSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="15" y1="40" x2="65" y2="40" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="55,30 65,40 55,50" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Frame - Adjustable frame rectangle (for showing end frame of push-in, etc.)
 */
export const FrameSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <rect x="20" y="20" width="40" height="40" stroke={color} strokeWidth="3" fill="none" />
  </svg>
);

/**
 * Boom Up - Simple vertical arrow pointing up
 */
export const BoomUpSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="40" y1="65" x2="40" y2="15" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="30,25 40,15 50,25" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Boom Down - Simple vertical arrow pointing down
 */
export const BoomDownSymbol: React.FC<OverlaySymbolProps> = ({
  color = '#FFFFFF',
  opacity = 0.8,
  size = 80,
}) => (
  <svg width={size} height={size} viewBox="0 0 80 80" style={{ opacity }}>
    <line x1="40" y1="15" x2="40" y2="65" stroke={color} strokeWidth="5" strokeLinecap="round" />
    <polyline points="30,55 40,65 50,55" stroke={color} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

/**
 * Registry of all overlay symbols
 * Used to look up the correct component based on movement type
 */
export const overlaySymbols: Record<CameraMovementType, React.FC<OverlaySymbolProps>> = {
  'pan-left': PanLeftSymbol,
  'pan-right': PanRightSymbol,
  'tilt-up': TiltUpSymbol,
  'tilt-down': TiltDownSymbol,
  'boom-up': BoomUpSymbol,
  'boom-down': BoomDownSymbol,
  'zoom-in': ZoomInSymbol,
  'zoom-out': ZoomOutSymbol,
  'push-in': PushInSymbol,
  'pull-out': PullBackSymbol,
  'truck-left': TruckLeftSymbol,
  'truck-right': TruckRightSymbol,
  'frame': FrameSymbol,
};

/**
 * Helper function to get human-readable name for movement type
 */
export function formatMovementName(type: CameraMovementType): string {
  const names: Record<CameraMovementType, string> = {
    'pan-left': 'Pan Left',
    'pan-right': 'Pan Right',
    'tilt-up': 'Tilt Up',
    'tilt-down': 'Tilt Down',
    'boom-up': 'Boom Up',
    'boom-down': 'Boom Down',
    'zoom-in': 'Zoom In',
    'zoom-out': 'Zoom Out',
    'push-in': 'Push In',
    'pull-out': 'Pull Back',
    'truck-left': 'Truck Left',
    'truck-right': 'Truck Right',
    'frame': 'Frame',
  };
  return names[type];
}
